import { useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabase';

export type AdminRole = 'owner' | 'admin' | 'editor' | 'hr' | 'support';

export interface AdminSession {
  session: Session;
  role: AdminRole;
  email: string;
}

/** Reads the role from JWT app_metadata. */
export function getRole(session: Session | null): AdminRole | null {
  if (!session) return null;
  const role = session.user.app_metadata?.role as string | undefined;
  if (role === 'owner' || role === 'admin' || role === 'editor' || role === 'hr' || role === 'support') {
    return role;
  }
  return null;
}

export function useAuth() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const role = getRole(session);

  return {
    session,
    user: session?.user ?? null,
    email: session?.user?.email ?? null,
    role,
    loading,
    isAuthenticated: !!session && !!role,
    signOut: () => supabase.auth.signOut(),
  };
}

/** Permission helper. Owner is always true; otherwise checks a per-entity map. */
export function can(role: AdminRole | null, action: 'manage_admins' | 'manage_settings' | 'manage_projects' | 'manage_blog' | 'manage_team' | 'manage_roles' | 'manage_contents' | 'manage_influencers' | 'view_inbox' | 'view_applications' | 'delete_applications'): boolean {
  if (!role) return false;
  if (role === 'owner') return true;
  switch (action) {
    case 'manage_admins':
      return false; // owner only
    case 'manage_settings':
      return role === 'admin';
    case 'manage_projects':
    case 'manage_blog':
    case 'manage_team':
      return role === 'admin' || role === 'editor';
    case 'manage_contents':
    case 'manage_influencers':
      return role === 'admin';
    case 'manage_roles':
    case 'view_applications':
      return role === 'admin' || role === 'hr';
    case 'delete_applications':
      return role === 'admin';
    case 'view_inbox':
      return role === 'admin' || role === 'support';
  }
}

export async function signInWithMagicLink(email: string, redirectTo: string) {
  return supabase.auth.signInWithOtp({
    email,
    options: { emailRedirectTo: redirectTo },
  });
}